/**
 * Project AI chat: ask questions about the project's Google Health data in plain language.
 * Backend: chatHealthData (Gemini on Vertex AI in Jamasp's Google Cloud project; read-only,
 * project-scoped BigQuery queries). Every answer shows the queries it ran, so it can be checked.
 */
import { httpsCallable } from 'firebase/functions';
import Markdown from 'markdown-to-jsx';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { functions } from '../../firebase';

const SUGGESTIONS = [
  'Which participants have data, for which dates, and from which devices?',
  'Average daily steps per participant on valid wear days.',
  'Average sleep duration and minutes of deep and REM sleep per participant.',
  'How does resting heart rate change across the study days?',
];

const mono = { fontFamily: 'var(--jp-mono)', fontSize: 12 };
const fmtBytes = (b) => (b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round((b || 0) / 1e3)} KB`);
const cell = (v) => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

function Preview({ rows }) {
  if (!rows?.length) return <div style={{ fontSize: 12, color: 'var(--jp-gray-600)' }}>No rows.</div>;
  const cols = Object.keys(rows[0]);
  const shown = rows.slice(0, 10);
  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--jp-gray-100)', borderRadius: 6 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
        <thead>
          <tr>{cols.map((c) => (
            <th key={c} style={{ ...mono, textAlign: 'left', padding: '5px 8px', background: 'var(--jp-gray-100)', whiteSpace: 'nowrap' }}>{c}</th>
          ))}</tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i} style={{ borderTop: '1px solid var(--jp-gray-100)' }}>
              {cols.map((c) => (
                <td key={c} style={{ ...mono, padding: '4px 8px', whiteSpace: 'nowrap', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell(r[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown.length && (
        <div style={{ fontSize: 11, color: 'var(--jp-gray-600)', padding: '4px 8px' }}>First {shown.length} of {rows.length} rows shown.</div>
      )}
    </div>
  );
}
Preview.propTypes = { rows: PropTypes.array };

function Queries({ queries }) {
  const [open, setOpen] = useState(false);
  if (!queries?.length) return null;
  const failed = queries.filter((q) => q.error).length;
  return (
    <div style={{ marginTop: 10 }}>
      <button type="button" className="ja-btn ja-btn-ghost" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => setOpen(!open)}>
        {open ? '▲' : '▼'} {queries.length} {queries.length === 1 ? 'query' : 'queries'}
        {failed ? ` (${failed} rejected or failed, then retried)` : ''}
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {queries.map((q, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} style={{ borderLeft: `3px solid ${q.error ? '#E57373' : 'var(--jp-blue-light)'}`, paddingLeft: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--jp-gray-600)', marginBottom: 4 }}>
                {i + 1}. {q.purpose || 'Query'}
                {q.error ? '' : ` · ${q.rowCount} row${q.rowCount === 1 ? '' : 's'} · ${fmtBytes(q.bytesProcessed)} scanned`}
              </div>
              <pre style={{ ...mono, background: 'var(--jp-gray-100)', borderRadius: 6, padding: 8, margin: '0 0 6px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{q.sql}</pre>
              {q.error
                ? <div style={{ fontSize: 12, color: '#B42318' }}>{q.error}</div>
                : <Preview rows={q.preview} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
Queries.propTypes = { queries: PropTypes.array };

function Message({ m }) {
  if (m.role === 'user') {
    return (
      <div style={{ alignSelf: 'flex-end', maxWidth: '80%', background: 'var(--jp-blue-light)', color: 'var(--jp-blue-dark)',
        borderRadius: '12px 12px 2px 12px', padding: '9px 13px', fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
        {m.text}
      </div>
    );
  }
  return (
    <div style={{ alignSelf: 'stretch', background: '#fff', border: '1px solid var(--jp-gray-200)', borderRadius: '12px 12px 12px 2px',
      padding: '11px 14px', fontSize: 14, lineHeight: 1.6, color: 'var(--jp-gray-800)' }}>
      {m.error
        ? <span style={{ color: '#B42318' }}>{m.text}</span>
        : <div className="ja-chat-md"><Markdown options={{ disableParsingRawHTML: true, forceBlock: true }}>{m.text}</Markdown></div>}
      <Queries queries={m.queries} />
    </div>
  );
}
Message.propTypes = { m: PropTypes.object.isRequired };

export default function HealthChat({ project }) {
  const [participants, setParticipants] = useState(null);
  const [who, setWho] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    let live = true;
    httpsCallable(functions, 'getProjectParticipants')({ projectId: project.id })
      .then((r) => { if (live) setParticipants((r.data?.participants || []).map((p) => p.uid || p.id)); })
      .catch(() => { if (live) setParticipants([]); });
    return () => { live = false; };
  }, [project.id]);

  useEffect(() => { endRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' }); }, [messages, busy]);

  const ask = async (text) => {
    const question = (text ?? draft).trim();
    if (!question || busy) return;
    // Earlier turns, as question-answer pairs (a failed answer drops its question too).
    const history = [];
    messages.forEach((m, i) => {
      const next = messages[i + 1];
      if (m.role === 'user' && next?.role === 'model' && !next.error) {
        history.push({ role: 'user', text: m.text }, { role: 'model', text: next.text });
      }
    });
    setMessages((ms) => [...ms, { role: 'user', text: question }]);
    setDraft('');
    setBusy(true);
    try {
      const res = await httpsCallable(functions, 'chatHealthData', { timeout: 300000 })({
        projectId: project.id,
        participantIds: who ? [who] : [],
        startDate: startDate || null,
        endDate: endDate || null,
        question,
        history,
      });
      setMessages((ms) => [...ms, { role: 'model', text: res.data.answer, queries: res.data.queries }]);
    } catch (err) {
      setMessages((ms) => [...ms, { role: 'model', error: true, text: `Could not answer: ${err.message}` }]);
    } finally {
      setBusy(false);
    }
  };

  const scopeText = `${who ? `participant ${who}` : 'all participants'}, ${startDate || endDate ? `${startDate || '…'} to ${endDate || '…'}` : 'all dates'}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        .ja-chat-md p { margin: 0 0 8px; } .ja-chat-md p:last-child { margin-bottom: 0; }
        .ja-chat-md ul, .ja-chat-md ol { margin: 0 0 8px; padding-left: 20px; }
        .ja-chat-md table { border-collapse: collapse; font-size: 13px; margin: 6px 0; }
        .ja-chat-md th, .ja-chat-md td { border: 1px solid var(--jp-gray-200); padding: 4px 8px; text-align: left; }
        .ja-chat-md code { font-family: var(--jp-mono); font-size: 12px; background: var(--jp-gray-100); padding: 1px 4px; border-radius: 4px; }
      `}</style>

      <div className="ja-card">
        <div className="ja-card-header">
          <h4 className="ja-card-title">Ask about this project&apos;s data</h4>
          <span className="ja-card-meta">Gemini on Vertex AI, inside Jamasp&apos;s Google Cloud project</span>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ fontSize: 13 }}>Participants<br />
            <select className="ja-select" value={who} onChange={(e) => setWho(e.target.value)} style={{ minWidth: 220 }} disabled={busy}>
              <option value="">All participants{participants ? ` (${participants.length})` : ''}</option>
              {(participants || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>From<br />
            <input type="date" className="ja-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={busy} />
          </label>
          <label style={{ fontSize: 13 }}>To (inclusive)<br />
            <input type="date" className="ja-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={busy} />
          </label>
          {messages.length > 0 && (
            <button type="button" className="ja-btn ja-btn-ghost" disabled={busy} onClick={() => setMessages([])}>New conversation</button>
          )}
        </div>
        <p style={{ fontSize: 12, color: 'var(--jp-gray-600)', margin: '12px 0 0', lineHeight: 1.5 }}>
          Answers come only from read-only queries on this project&apos;s Google Health tables ({scopeText}); open
          &ldquo;queries&rdquo; under an answer to check them. Vertex AI does not use this data to train models.
        </p>
      </div>

      <div className="ja-card" style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 260 }}>
        {messages.length === 0 && !busy && (
          <div>
            <div style={{ fontSize: 13, color: 'var(--jp-gray-600)', marginBottom: 10 }}>Try one of these, or ask your own:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="ja-btn ja-btn-outline" style={{ fontSize: 12, textAlign: 'left' }} onClick={() => ask(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {/* eslint-disable-next-line react/no-array-index-key */}
        {messages.map((m, i) => <Message key={i} m={m} />)}
        {busy && (
          <div style={{ fontSize: 13, color: 'var(--jp-gray-600)' }}>
            Querying the data and writing an answer; this can take up to a minute.
            <div className="ja-progress" style={{ marginTop: 8 }}><div className="ja-progress-bar" /></div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="ja-card" style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <textarea
          className="ja-textarea"
          rows={2}
          style={{ flex: 1, resize: 'vertical', fontSize: 14 }}
          placeholder="Ask a question (Enter sends; Shift+Enter adds a line)"
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
        />
        <button type="button" className="ja-btn ja-btn-primary" disabled={busy || !draft.trim()} onClick={() => ask()}>Send</button>
      </div>
    </div>
  );
}

HealthChat.propTypes = {
  project: PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string }).isRequired,
};
